import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { HardDrive, RefreshCw, Video, Users, FolderKanban, Plane, Clock, Database } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = '/api';

const SettingsPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [stats, setStats] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [statsRes, recsRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`, { headers }),
        axios.get(`${API_URL}/recordings`, { headers })
      ]);
      setStats(statsRes.data);
      setRecordings(recsRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const totalSize = recordings.reduce((acc, r) => acc + (r.file_size || 0), 0);
  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes > 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    if (bytes > 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-3 border-gold/30 border-t-gold rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-rajdhani font-semibold text-navy uppercase tracking-wider flex items-center gap-2">
          <HardDrive size={20} className="text-gold" /> SISTEMA
        </h2>
        <Button onClick={fetchData} variant="ghost" size="icon" className="text-gray-500 hover:text-gold" data-testid="refresh-stats">
          <RefreshCw size={16} />
        </Button>
      </div>

      {/* Storage Info */}
      <div className="card-hud p-6 animate-slide-up">
        <h3 className="text-navy font-rajdhani font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
          <Database size={18} className="text-gold" /> ARMAZENAMENTO LOCAL
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-gray-500 text-xs font-mono uppercase mb-1">GRAVACOES SALVAS</p>
            <p className="text-2xl font-rajdhani font-bold text-navy">{recordings.length}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-gray-500 text-xs font-mono uppercase mb-1">ESPACO UTILIZADO</p>
            <p className="text-2xl font-rajdhani font-bold text-gold">{formatSize(totalSize)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-gray-500 text-xs font-mono uppercase mb-1">GRAVACOES PRONTAS</p>
            <p className="text-2xl font-rajdhani font-bold text-green-600">{recordings.filter(r => r.status === 'ready').length}</p>
          </div>
        </div>
        <p className="text-gray-400 text-xs font-mono mt-4">As gravacoes sao armazenadas localmente no servidor em /recordings</p>
      </div>

      {/* System Stats */}
      {stats && (
        <div className="card-hud p-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <h3 className="text-navy font-rajdhani font-semibold uppercase tracking-wider mb-4">RESUMO DO SISTEMA</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
              <FolderKanban size={20} className="text-gold mx-auto mb-2" />
              <p className="text-2xl font-rajdhani font-bold text-navy">{stats.projects_count}</p>
              <p className="text-gray-500 text-xs font-mono uppercase">Projetos</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
              <Plane size={20} className="text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-rajdhani font-bold text-navy">{stats.drones_count}</p>
              <p className="text-gray-500 text-xs font-mono uppercase">Drones</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
              <Users size={20} className="text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-rajdhani font-bold text-navy">{stats.users_count}</p>
              <p className="text-gray-500 text-xs font-mono uppercase">Usuarios</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
              <Video size={20} className="text-red-500 mx-auto mb-2" />
              <p className="text-2xl font-rajdhani font-bold text-navy">{recordings.length}</p>
              <p className="text-gray-500 text-xs font-mono uppercase">Gravacoes</p>
            </div>
          </div>
        </div>
      )}

      {/* System Info */}
      <div className="card-hud p-6 animate-slide-up" style={{ animationDelay: '0.2s' }}>
        <h3 className="text-navy font-rajdhani font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
          <Clock size={18} className="text-gold" /> INFORMACOES
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500 text-sm font-mono">Sistema</span>
            <span className="text-navy text-sm font-medium">RPAS Monitor - Gestao de RPAS</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500 text-sm font-mono">Armazenamento</span>
            <span className="text-navy text-sm font-medium">Servidor Local</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500 text-sm font-mono">Gravacao</span>
            <span className="text-navy text-sm font-medium">ffmpeg + Browser (RecordRTC)</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-gray-500 text-sm font-mono">Seguranca</span>
            <span className="text-navy text-sm font-medium">JWT + LGPD (Clock-in Piloto)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
